import {
  Badge,
  Checkbox,
  Group,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text
} from '@mantine/core'
import { ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SelectableCard } from '@/components/ui-shared/SelectableCard/SelectableCard'

export type MilestoneCameraRow = {
  camera_id: string
  name: string
  enabled: boolean
  mac_address?: string
  // Saved on the pipeline but no longer returned by the camera source.
  missing: boolean
  offline?: boolean
  hideId?: boolean
}

type MilestoneCameraListProps = {
  rows: MilestoneCameraRow[]
  selectedIds: Set<string>
  onToggle: (row: MilestoneCameraRow) => void
  onToggleAll: (rows: MilestoneCameraRow[], select: boolean) => void
  missingLabel?: ReactNode
}

export const MilestoneCameraList = ({
  rows,
  selectedIds,
  onToggle,
  onToggleAll,
  missingLabel = <FormattedMessage id="vlm.pipelines.create.cameras.missing" />
}: MilestoneCameraListProps) => {
  const intl = useIntl()
  const selectable = rows.filter((row) => row.enabled)
  const selectedCount = selectable.filter((row) =>
    selectedIds.has(row.camera_id)
  ).length
  const allSelected =
    selectable.length > 0 && selectedCount === selectable.length

  if (rows.length === 0)
    return (
      <Text size="sm" c="dimmed" ta="center" py="lg">
        <FormattedMessage id="vlm.pipelines.create.cameras.empty" />
      </Text>
    )

  return (
    <Stack gap="sm">
      <Checkbox
        label={intl.formatMessage({ id: 'selectAll' })}
        checked={allSelected}
        indeterminate={selectedCount > 0 && !allSelected}
        disabled={selectable.length === 0}
        onChange={() => onToggleAll(selectable, !allSelected)}
      />

      <ScrollArea.Autosize mah={420}>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} p={2}>
          {rows.map((row) => (
            <SelectableCard
              key={row.camera_id}
              height={84}
              isSelected={selectedIds.has(row.camera_id)}
              disabled={!row.enabled}
              onClick={() => onToggle(row)}
            >
              <Stack gap={4} justify="center" h="100%">
                <Text size="sm" fw={600} lineClamp={1}>
                  {row.name}
                </Text>
                {!row.hideId && (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {row.camera_id}
                  </Text>
                )}
                {(!row.enabled || row.missing || row.offline) && (
                  <Group gap={4}>
                    {!row.enabled && (
                      <Badge size="xs" color="gray" variant="light">
                        <FormattedMessage id="vlm.pipelines.create.cameras.disabled" />
                      </Badge>
                    )}
                    {row.missing && (
                      <Badge size="xs" color="orange" variant="light">
                        {missingLabel}
                      </Badge>
                    )}
                    {row.offline && (
                      <Badge size="xs" color="orange" variant="light">
                        <FormattedMessage id="vlm.pipelines.create.cameras.registered.offline" />
                      </Badge>
                    )}
                  </Group>
                )}
              </Stack>
            </SelectableCard>
          ))}
        </SimpleGrid>
      </ScrollArea.Autosize>
    </Stack>
  )
}
